"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  FaCheck,
  FaStar,
  FaAward,
  FaTrophy,
  FaHeart,
  FaShieldAlt,
  FaCertificate,
  FaUserMd,
  FaHospital,
  FaPhone,
  FaClock,
  FaMapMarkerAlt,
} from "react-icons/fa";

interface Callout {
  text: string;
  icon: string;
  linkUrl?: string;
}

interface SuperTopBarProps {
  staticText?: string;
  callouts?: Callout[];
  enabled?: boolean;
}

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  FaCheck,
  FaStar,
  FaAward,
  FaTrophy,
  FaHeart,
  FaShieldAlt,
  FaCertificate,
  FaUserMd,
  FaHospital,
  FaPhone,
  FaClock,
  FaMapMarkerAlt,
};

export default function SuperTopBar({
  staticText = "Why Trichos?",
  callouts = [],
  enabled = true,
}: SuperTopBarProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const [trackWidth, setTrackWidth] = useState(0);

  // Duplicate callouts for seamless looping (3x for smooth infinite scroll)
  const duplicatedCallouts = [...callouts, ...callouts, ...callouts];

  useEffect(() => {
    if (!enabled || callouts.length === 0) return;

    const updateTrackWidth = () => {
      if (trackRef.current) {
        const width = trackRef.current.scrollWidth / 3; // Divide by 3 since we duplicated 3x
        setTrackWidth(width);
      }
    };

    updateTrackWidth();
    window.addEventListener("resize", updateTrackWidth);

    const animate = () => {
      setOffset((prev) => {
        const newOffset = prev + 0.5; // Adjust speed here (pixels per frame)
        // Reset when we've scrolled one full set of callouts
        if (newOffset >= trackWidth) {
          return 0;
        }
        return newOffset;
      });
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener("resize", updateTrackWidth);
    };
  }, [enabled, callouts.length, trackWidth]);

  if (!enabled || callouts.length === 0) {
    return null;
  }

  return (
    <section
      className="w-full border-b border-gray-200 bg-gray-50"
      style={{ overflowX: "hidden", maxWidth: "100vw" }}
    >
      <div
        className="flex items-center overflow-hidden"
        style={{ width: "100%", maxWidth: "100%" }}
      >
        {/* Static Left Section */}
        <div className="flex shrink-0 items-center gap-3 border-r border-gray-200 bg-gray-50 px-4 py-2.5">
          <span className="whitespace-nowrap text-xs font-semibold text-gray-900">
            {staticText}
          </span>
        </div>

        {/* Scrolling Callouts Section */}
        <div
          className="relative flex-1 overflow-hidden"
          style={{ minWidth: 0 }}
        >
          <div
            ref={trackRef}
            className="flex"
            style={{
              transform: `translateX(-${offset}px)`,
              willChange: "transform",
            }}
          >
            {duplicatedCallouts.map((callout, index) => {
              const IconComponent = iconMap[callout.icon] || FaCheck;
              const calloutContent = (
                <div className="flex shrink-0 items-center gap-2 px-12 py-2.5">
                  <IconComponent className="size-3.5 text-gray-600" />
                  <span className="whitespace-nowrap text-xs text-gray-700">
                    {callout.text}
                  </span>
                </div>
              );

              return (
                <div key={`${callout.text}-${index}`}>
                  {callout.linkUrl ? (
                    <Link
                      href={callout.linkUrl}
                      className="block transition-colors hover:bg-gray-50"
                    >
                      {calloutContent}
                    </Link>
                  ) : (
                    calloutContent
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
